/**
 * THE INVISIBLE GUEST TOKEN, MINTED (th-mpimer.26; launch-marketing-pages design M7
 * "Guests"; stress pass 3 ruling L1).
 *
 * A guest takes the showcase lane only with a guest token, and the token is minted
 * after the visitor's first genuine interaction: ONE passive listener for a scroll, a
 * pointer move or a key press, removed once it fires. It POSTs
 * `/api/v2/showcases/guest_token` through `services/apiClient.js` and hands
 * `{ token, expiresAt }` to `services/showcaseLane.js#setGuestToken`, which holds it,
 * sends it as `X-Guest-Token` and drops it at its expiry and on every `authTransition`.
 * A script that never interacts mints nothing and reads the guest baseline; a person
 * is never asked anything.
 *
 * THE TOKEN IS A CHEAP FILTER, NOT A WALL. A browser-driving agent mints it as a person
 * does, and stays bounded by one read per fact per UTC day and the daily cap.
 *
 * WHEN THE LISTENER IS ARMED. Only while no session bearer is held and no guest token
 * is: at start, once a held token leaves (its expiry, heard as the lane identity
 * leaving `guest`), and after every `authTransition`, read once the transition has
 * settled (a sign-out drops the bearer just after it dispatches). A mint answered after
 * an auth transition is discarded, and a failed mint re-arms after `RETRY_DELAY_MS`, so
 * a refusing server sees at most one mint a minute from a tab.
 *
 * Started once from the thin shell (`entrypoints/App.vue`). It imports only boot
 * modules (the two lane leaves and the API client), never a workspace store (the
 * boot-graph rule, th-jki5kv.8).
 */
import { useApiClient } from "@/services/apiClient";
import { readSessionToken } from "@/services/sessionToken";
import { holdsGuestToken, onLaneChange, setGuestToken } from "@/services/showcaseLane";

export const GUEST_TOKEN_ENDPOINT = "/showcases/guest_token";

// A genuine interaction. Captured on the window, so a scroll inside any scrolling
// container counts, and passive, so it never delays one.
export const INTERACTION_EVENTS = Object.freeze(["scroll", "pointermove", "keydown"]);
const LISTENER_OPTIONS = Object.freeze({ capture: true, passive: true });

export const RETRY_DELAY_MS = 60_000;

let started = false;
let armed = false;
let retryTimer = null;
let unsubscribeLane = null;
// Bumped on every auth transition, so a mint sent under one identity is dropped if it
// answers under the next.
let generation = 0;

function arm() {
  if (!started || armed || holdsGuestToken() || readSessionToken()) return;
  armed = true;
  INTERACTION_EVENTS.forEach((type) => window.addEventListener(type, onInteraction, LISTENER_OPTIONS));
}

function disarm() {
  if (!armed) return;
  armed = false;
  INTERACTION_EVENTS.forEach((type) => window.removeEventListener(type, onInteraction, LISTENER_OPTIONS));
}

function onInteraction() {
  disarm();
  mint();
}

async function mint() {
  if (readSessionToken()) return;
  const minting = generation;
  let answer;
  try {
    answer = await useApiClient().post(GUEST_TOKEN_ENDPOINT, {});
  } catch {
    if (minting === generation) {
      retryTimer = setTimeout(() => {
        retryTimer = null;
        arm();
      }, RETRY_DELAY_MS);
    }
    return;
  }
  // A 204 (the server reads this request as signed in) answers null and carries no token.
  const token = answer?.data?.token;
  if (!started || minting !== generation || readSessionToken() || !token) return;
  setGuestToken(token, { expiresAt: answer.data.expires_at });
}

function onAuthTransition() {
  generation += 1;
  clearTimeout(retryTimer);
  retryTimer = null;
  disarm();
  queueMicrotask(arm);
}

/** Start minting: arm the interaction listener once. A second call does nothing. */
export function startGuestToken() {
  if (started || typeof window === "undefined") return;
  started = true;
  unsubscribeLane = onLaneChange(() => {
    if (!holdsGuestToken()) arm();
  });
  window.addEventListener("authTransition", onAuthTransition);
  arm();
}

/** Stop minting: remove every listener and timer `startGuestToken` installed. */
export function stopGuestToken() {
  if (!started) return;
  disarm();
  started = false;
  clearTimeout(retryTimer);
  retryTimer = null;
  unsubscribeLane?.();
  unsubscribeLane = null;
  window.removeEventListener("authTransition", onAuthTransition);
}
